import Cta from '@atoms/Cta/Cta';
import Drawer, { IDrawer } from '@atoms/Drawer/Drawer';
import Icon from '@atoms/Icon/Icon';
import Text from '@atoms/Text/Text';
import { ILink } from '@models/ILink';
import classNames from 'classnames';
import { PropsWithChildren, useEffect, useState } from 'react';

export interface IDrawerWrapper extends IDrawer {
  readonly title?: string;
  readonly description?: string;
  readonly wrapperStyles?: string;
  readonly cta?: ILink;
  readonly sizeVariant?: 'small' | 'large';
  readonly containerClasses?: string;
  readonly visibleOverflow?: boolean;
}

const DrawerWrapper = ({
  animateVerticallyOnMobile = true,
  children,
  closeLabel,
  containerClasses,
  cta,
  description,
  isOpen,
  setIsOpen,
  sizeVariant = 'small',
  title,
  visibleOverflow,
  wrapperStyles = 'tw-px-4 tw-py-10 md:tw-p-12',
  ...rest
}: PropsWithChildren<IDrawerWrapper>) => {
  const [isRtl, setIsRtl] = useState(false);

  useEffect(() => {
    const dir = document.documentElement.getAttribute('dir');
    setIsRtl(dir === 'rtl');
  }, []);

  const closeButton = (
    <button
      type="button"
      aria-label={closeLabel}
      className={classNames(
        'tw-absolute tw-top-xs tw-z-max',
        isRtl ? ' tw-left-xs' : ' tw-right-xs',
      )}
      onClick={() => setIsOpen(false)}
    >
      <Icon name="cross" className="tw-text-2xl" size="1.5rem" />
    </button>
  );

  return (
    <Drawer
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      animateVerticallyOnMobile={animateVerticallyOnMobile}
      isRtl={isRtl}
      sizeVariant={sizeVariant}
      closeLabel={closeLabel}
      {...rest}
    >
      <div className="tw-flex tw-h-full tw-w-full tw-flex-col">
        {/* Fixed header */}
        {title || description || cta ? (
          <div
            className={classNames(
              'tw-sticky tw-top-[-1px] tw-z-50 tw-bg-grey-10 tw-px-4 tw-py-6 md:tw-px-12 md:tw-py-10',
              containerClasses,
            )}
          >
            {title && (
              <Text variant="heading-28" className="tw-my-auto">
                {title}
              </Text>
            )}
            {description && <Text variant="body-small">{description}</Text>}
            {cta?.text && cta.url && (
              <div className="tw-pt-6">
                <Cta {...cta} variant="grey" />
              </div>
            )}

            {closeButton}
          </div>
        ) : (
          closeButton
        )}

        {/* Scrollable content */}
        <div className="tw-relative tw-bottom-0 tw-left-0 tw-h-full tw-w-full">
          <div
            className={classNames(
              'tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-gap-y-lg',
              visibleOverflow ? 'tw-overflow-visible' : 'tw-overflow-y-auto',
            )}
          >
            <div className={wrapperStyles}>{children}</div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default DrawerWrapper;
